import * as DocumentPicker from 'expo-document-picker';
import { type ChangeEvent, type CSSProperties, type DragEvent, useState } from 'react';
import { ActivityIndicator, Platform, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { Badge } from '../../../components/primitives/Badge';
import { Button } from '../../../components/primitives/Button';
import { FormInput } from '../../../components/primitives/FormInput';
import { Sheet } from '../../../components/primitives/Sheet';
import { HStack, VStack } from '../../../components/primitives/Stack';
import { Body, Caption, Heading2, Label } from '../../../components/primitives/Text';
import { useToast } from '../../../contexts/ToastContext';
import { useTranslation } from '../../../contexts/TranslationContext';
import { useResponsive } from '../../../hooks/useResponsive';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import { darkPrecisionFontFamilies, fieldPlaceholderColor } from '../../../theme/typography';
import { useRegulatoryBundles, useUploadRegulatoryBundle } from '../hooks';
import type {
  RegulatoryBundleStatus,
  RegulatoryBundleSubmission,
  RegulatoryDocumentType,
} from '../types';

const DOCUMENT_TYPES: RegulatoryDocumentType[] = ['rfc', 'tax_id', 'nom_184', 'rut', 'other'];

const STATUS_LABEL: Record<RegulatoryBundleStatus, string> = {
  uploaded: 'Uploaded',
  in_review: 'In review',
  approved: 'Approved',
  rejected: 'Rejected',
};

const STATUS_COLOR: Record<RegulatoryBundleStatus, string> = {
  uploaded: colors.text.tertiary,
  in_review: colors.status.warning.main,
  approved: colors.status.success.main,
  rejected: colors.status.error.main,
};

const STATUS_BADGE_COLOR: Record<RegulatoryBundleStatus, 'neutral' | 'amber' | 'green' | 'red'> = {
  uploaded: 'neutral',
  in_review: 'amber',
  approved: 'green',
  rejected: 'red',
};

const TABLE_COLUMNS = [
  'Country/Type',
  'Status',
  'File',
  'Submitted',
  'Reviewed',
  'Action',
] as const;

const ACCEPTED_FILE_TYPES = ['application/pdf', 'image/png', 'image/jpeg'];
const ACCEPTED_FILE_EXTENSIONS = '.pdf,.png,.jpg,.jpeg';
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

type FileValidationError = 'tooLarge' | 'unsupportedType';

function getFileValidationError(file: File): FileValidationError | null {
  if (file.size > MAX_FILE_SIZE_BYTES) return 'tooLarge';
  if (!ACCEPTED_FILE_TYPES.includes(file.type)) return 'unsupportedType';
  return null;
}

function handleUploadFailureGlobally() {}

function formatSubmissionDate(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString();
}

interface DetailRowProps {
  label: string;
  value: string;
}

function DetailRow({ label, value }: Readonly<DetailRowProps>) {
  return (
    <HStack spacing="sm" justify="between">
      <Caption style={styles.detailLabel}>{label}</Caption>
      <Label style={styles.detailValue}>{value}</Label>
    </HStack>
  );
}

interface RegulatoryDocsPanelProps {
  organizationId: string;
}

export function RegulatoryDocsPanel({ organizationId }: Readonly<RegulatoryDocsPanelProps>) {
  const { data, isPending, isError, error } = useRegulatoryBundles(organizationId);
  const uploadMutation = useUploadRegulatoryBundle(organizationId);
  const { isMobile } = useResponsive();
  const { t } = useTranslation();
  const { showSuccess, showError } = useToast();

  const [country, setCountry] = useState('');
  const [documentType, setDocumentType] = useState<RegulatoryDocumentType>('rfc');
  const [pickedFile, setPickedFile] = useState<{ file: File; name: string } | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  function selectFile(file: File) {
    const validationError = getFileValidationError(file);
    if (validationError) {
      const message =
        validationError === 'tooLarge'
          ? t('voiceAgents.regulatoryDocs.fileTooLarge')
          : t('voiceAgents.regulatoryDocs.unsupportedFileType');
      setFormError(message);
      showError(message);
      return;
    }
    setFormError(null);
    setPickedFile({ file, name: file.name });
  }

  async function pickFile() {
    setFormError(null);
    const result = await DocumentPicker.getDocumentAsync({
      type: ACCEPTED_FILE_TYPES,
      copyToCacheDirectory: true,
    });
    if (result.canceled || !result.assets?.[0]) return;
    const asset = result.assets[0];
    // On web, the asset.file is already a File. On native, fetch the URI.
    if ((asset as { file?: File }).file) {
      const f = (asset as { file: File }).file;
      setPickedFile({ file: f, name: asset.name });
      return;
    }
    const res = await fetch(asset.uri);
    const blob = await res.blob();
    const file = new File([blob], asset.name, {
      type: asset.mimeType ?? 'application/octet-stream',
    });
    setPickedFile({ file, name: asset.name });
  }

  function handleWebFileChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (file) selectFile(file);
  }

  function handleDrop(event: DragEvent) {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files[0];
    if (file) selectFile(file);
  }

  function handleDragOver(event: DragEvent) {
    event.preventDefault();
    setIsDragging(true);
  }

  function handleDragLeave() {
    setIsDragging(false);
  }

  async function handleUpload() {
    setFormError(null);
    if (!country.trim() || country.length !== 2) {
      setFormError(t('voiceAgents.regulatoryDocs.countryInvalid'));
      return;
    }
    if (!pickedFile) {
      setFormError(t('voiceAgents.regulatoryDocs.pickFilePrompt'));
      return;
    }
    try {
      await uploadMutation.mutateAsync({
        country: country.toUpperCase(),
        documentType,
        file: pickedFile.file,
      });
      setCountry('');
      setDocumentType('rfc');
      setPickedFile(null);
      showSuccess(t('voiceAgents.regulatoryDocs.uploadSuccess'));
    } catch {
      handleUploadFailureGlobally();
    }
  }

  const sortedData: RegulatoryBundleSubmission[] = (data ?? []).slice();
  const selectedSubmission = sortedData.find((row) => row.id === selectedId) ?? null;

  return (
    <ScrollView style={styles.root} contentContainerStyle={styles.content}>
      <VStack spacing="lg">
        <Heading2 accessibilityRole="header">Regulatory documents</Heading2>
        <Caption style={styles.subtitle}>
          Upload the regulatory bundle (RFC, NOM-184, RUT, …) that lets FlowSoft provision a local
          caller ID for your organization in each country.
        </Caption>

        <View style={styles.uploadCard} accessibilityLabel="Upload new document">
          <VStack spacing="md">
            <VStack spacing="xs">
              <Label style={styles.label}>Country (ISO-2, e.g. MX, CO)</Label>
              <FormInput
                value={country}
                onChangeText={(v) => setCountry(v.toUpperCase())}
                placeholder="MX"
                placeholderTextColor={fieldPlaceholderColor}
                maxLength={2}
                autoCapitalize="characters"
                style={styles.input}
                accessibilityLabel="Country code"
              />
            </VStack>

            <VStack spacing="xs">
              <Label style={styles.label}>Document type</Label>
              <HStack spacing="sm" accessibilityRole="radiogroup">
                {DOCUMENT_TYPES.map((dt) => (
                  <Pressable
                    key={dt}
                    onPress={() => setDocumentType(dt)}
                    style={[styles.pill, documentType === dt && styles.pillSelected]}
                    accessibilityRole="radio"
                    accessibilityState={{ checked: documentType === dt }}
                    accessibilityLabel={dt}
                  >
                    <Caption style={styles.pillLabel}>{dt}</Caption>
                  </Pressable>
                ))}
              </HStack>
            </VStack>

            <VStack spacing="xs">
              <Label style={styles.label}>File (PDF, PNG, JPEG · max 10 MB)</Label>
              {Platform.OS === 'web' ? (
                // The label/input pairing lets a native click on the label open
                // the file dialog without an imperative ref — real drag-and-drop
                // is handled by onDrop/onDragOver/onDragLeave on the label itself.
                <label
                  data-testid="regulatory_docs_dropzone"
                  aria-label={
                    pickedFile
                      ? `Replace file — ${pickedFile.name}`
                      : 'Drag and drop a file, or click to browse'
                  }
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  style={{
                    ...webStyles.dropzone,
                    ...(isDragging ? webStyles.dropzoneActive : {}),
                  }}
                >
                  {pickedFile
                    ? `Selected: ${pickedFile.name} — drop or click to replace`
                    : 'Drag & drop a file here, or click to browse'}
                  <input
                    type="file"
                    accept={ACCEPTED_FILE_EXTENSIONS}
                    onChange={handleWebFileChange}
                    style={{ display: 'none' }}
                  />
                </label>
              ) : (
                <HStack spacing="sm">
                  <Button
                    variant="outline"
                    size="sm"
                    onPress={pickFile}
                    accessibilityLabel="Pick a file"
                  >
                    {pickedFile ? 'Replace file' : 'Pick file'}
                  </Button>
                  {pickedFile ? <Caption style={styles.fileName}>{pickedFile.name}</Caption> : null}
                </HStack>
              )}
            </VStack>

            {formError ? (
              <Body style={styles.error} accessibilityRole="alert">
                {formError}
              </Body>
            ) : null}

            <Button
              variant="primary"
              onPress={handleUpload}
              disabled={uploadMutation.isPending}
              accessibilityLabel="Upload document"
            >
              {uploadMutation.isPending ? (
                <ActivityIndicator size="small" color={colors.white} />
              ) : (
                'Upload'
              )}
            </Button>
          </VStack>
        </View>

        <Heading2 accessibilityRole="header" style={styles.sectionTitle}>
          Submissions
        </Heading2>

        {isPending ? (
          <View style={styles.loading}>
            <ActivityIndicator color={colors.primary.main} />
          </View>
        ) : null}

        {isError ? (
          <Body style={styles.error} accessibilityRole="alert">
            Could not load submissions: {error instanceof Error ? error.message : 'unknown error'}
          </Body>
        ) : null}

        {data && data.length === 0 ? (
          <Body style={styles.empty}>No submissions yet. Upload the first one above.</Body>
        ) : null}

        {sortedData.length > 0 ? (
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator
            testID="regulatory_docs_table"
            accessibilityLabel="Regulatory submissions table"
          >
            <View>
              <View style={styles.headerRow}>
                {TABLE_COLUMNS.map((column) => (
                  <View key={column} style={styles.cell}>
                    <Caption color={colors.primary.main} numberOfLines={1}>
                      {column}
                    </Caption>
                  </View>
                ))}
              </View>

              {sortedData.map((row, index) => (
                <Pressable
                  key={row.id}
                  onPress={() => setSelectedId(row.id)}
                  accessibilityRole="button"
                  accessibilityLabel={`View details — ${row.fileName}`}
                  testID={`regulatory_docs_row_${row.id}`}
                  style={[styles.dataRow, index % 2 === 0 && styles.dataRowEven]}
                >
                  <View style={styles.cell}>
                    <Label style={styles.rowCode} numberOfLines={1}>
                      {row.country} · {row.documentType}
                    </Label>
                  </View>
                  <View style={styles.cell}>
                    <Caption style={[styles.statusBadge, { color: STATUS_COLOR[row.status] }]}>
                      {STATUS_LABEL[row.status]}
                    </Caption>
                  </View>
                  <View style={styles.cell}>
                    <Caption numberOfLines={1}>{row.fileName}</Caption>
                  </View>
                  <View style={styles.cell}>
                    <Caption numberOfLines={1}>{formatSubmissionDate(row.submittedAt)}</Caption>
                  </View>
                  <View style={styles.cell}>
                    <Caption numberOfLines={1}>{formatSubmissionDate(row.reviewedAt)}</Caption>
                  </View>
                  <View style={styles.cell}>
                    <Caption style={styles.actionLabel}>View details</Caption>
                  </View>
                </Pressable>
              ))}
            </View>
          </ScrollView>
        ) : null}
      </VStack>

      <Sheet
        visible={selectedSubmission !== null}
        onClose={() => setSelectedId(null)}
        title={
          selectedSubmission
            ? `${selectedSubmission.country} · ${selectedSubmission.documentType}`
            : undefined
        }
        position={isMobile ? 'bottom' : 'right'}
        size="md"
      >
        {selectedSubmission ? (
          <VStack spacing="lg">
            <Badge color={STATUS_BADGE_COLOR[selectedSubmission.status]} variant="subtle">
              {STATUS_LABEL[selectedSubmission.status]}
            </Badge>

            <VStack spacing="sm">
              <DetailRow label="Country" value={selectedSubmission.country} />
              <DetailRow label="Document type" value={selectedSubmission.documentType} />
              <DetailRow label="File" value={selectedSubmission.fileName} />
              <DetailRow
                label="Size"
                value={`${(selectedSubmission.fileSizeBytes / 1024).toFixed(1)} KB`}
              />
              <DetailRow
                label="Submitted"
                value={formatSubmissionDate(selectedSubmission.submittedAt)}
              />
              <DetailRow
                label="Reviewed"
                value={formatSubmissionDate(selectedSubmission.reviewedAt)}
              />
            </VStack>

            <VStack spacing="xs">
              <Label style={styles.label}>Reviewer note</Label>
              <Body style={styles.reviewerNoteFull} accessibilityLabel="Reviewer note">
                {selectedSubmission.reviewerNote ?? 'No reviewer note yet.'}
              </Body>
            </VStack>
          </VStack>
        ) : null}
      </Sheet>
    </ScrollView>
  );
}

const webStyles = {
  dropzone: {
    border: `2px dashed ${colors.background.elevated}`,
    borderRadius: tokens.radius.lg,
    padding: tokens.spacing.lg,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacing.sm,
    backgroundColor: colors.background.input,
    minHeight: 96,
    cursor: 'pointer',
    color: colors.text.secondary,
    fontSize: tokens.fontSize.sm,
  } as CSSProperties,
  dropzoneActive: {
    borderColor: colors.primary.main,
    backgroundColor: colors.background.elevated,
  } as CSSProperties,
};

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: colors.background.primary },
  content: { padding: tokens.spacing.xl, paddingBottom: 80, gap: tokens.spacing.sm },
  subtitle: { color: colors.text.tertiary, lineHeight: 20 },
  sectionTitle: { marginTop: tokens.spacing.lg },
  uploadCard: {
    backgroundColor: colors.background.secondary,
    borderRadius: tokens.radius.mdPlus,
    padding: tokens.spacing.lgPlus,
  },
  label: { color: colors.text.secondary, fontSize: tokens.fontSize.sm },
  input: {
    backgroundColor: colors.background.input,
    borderRadius: tokens.radius.md,
    borderWidth: 1,
    borderColor: colors.background.elevated,
    paddingHorizontal: tokens.spacing.mdPlus,
    paddingVertical: tokens.spacing.md,
    color: colors.text.primary,
    fontSize: tokens.fontSize.basePlus,
  },
  pill: {
    paddingHorizontal: tokens.spacing.md,
    paddingVertical: tokens.spacing.sm,
    borderRadius: tokens.radius.full,
    borderWidth: 1,
    borderColor: colors.background.elevated,
    backgroundColor: colors.background.input,
  },
  pillSelected: {
    borderColor: colors.primary.main,
    backgroundColor: colors.background.elevated,
  },
  pillLabel: {
    color: colors.text.primary,
    fontSize: tokens.fontSize.sm,
    fontFamily: darkPrecisionFontFamilies.metric,
  },
  fileName: { color: colors.text.tertiary, alignSelf: 'center' },
  error: { color: colors.status.error.main, lineHeight: 20 },
  empty: { color: colors.text.tertiary, lineHeight: 20 },
  loading: { paddingVertical: tokens.spacing.xl, alignItems: 'center' },
  headerRow: {
    flexDirection: 'row',
    backgroundColor: colors.background.tertiary,
    borderTopLeftRadius: tokens.radius.md,
    borderTopRightRadius: tokens.radius.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.background.elevated,
  },
  dataRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  dataRowEven: {
    backgroundColor: colors.background.secondary,
  },
  cell: {
    width: 150,
    paddingHorizontal: tokens.spacing.md,
    paddingVertical: tokens.spacing.sm,
  },
  rowCode: {
    color: colors.text.primary,
    fontSize: tokens.fontSize.base,
    fontFamily: darkPrecisionFontFamilies.metric,
  },
  statusBadge: { fontSize: tokens.fontSize.xs, fontWeight: '600', textTransform: 'uppercase' },
  actionLabel: { color: colors.primary.main, fontWeight: '600' },
  detailLabel: { color: colors.text.tertiary, flex: 1 },
  detailValue: { color: colors.text.primary, flex: 2, textAlign: 'right' },
  reviewerNoteFull: {
    color: colors.text.secondary,
    lineHeight: 20,
    backgroundColor: colors.background.elevated,
    borderRadius: tokens.radius.md,
    padding: tokens.spacing.md,
  },
});
